import type { AuthTokens, SessionUser } from '@/types/api';

const KEY = 'olefleet.session';

export interface StoredSession {
  accessToken: string;
  refreshToken: string;
  /** Epoch ms at which the access token stops being accepted. */
  expiresAt: number;
  user: SessionUser;
}

/**
 * "Remember me" decides which Storage the session lands in. Everything else is
 * identical, so the rest of the app never has to know which one is in play.
 */
function stores(): Storage[] {
  try {
    return [window.localStorage, window.sessionStorage];
  } catch {
    return [];
  }
}

export function readSession(): StoredSession | null {
  for (const store of stores()) {
    try {
      const raw = store.getItem(KEY);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as StoredSession;
      if (parsed?.accessToken && parsed?.refreshToken && parsed?.user) return parsed;
    } catch {
      /* corrupt entry — fall through to the next store */
    }
  }
  return null;
}

export function writeSession(tokens: AuthTokens, remember: boolean): StoredSession {
  const session: StoredSession = {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresAt: Date.now() + tokens.expiresIn * 1000,
    user: tokens.user,
  };
  clearSession();
  try {
    (remember ? window.localStorage : window.sessionStorage).setItem(KEY, JSON.stringify(session));
  } catch {
    /* private mode: the session simply does not survive a reload */
  }
  return session;
}

/** Refresh rotates both tokens; keep them wherever the session already lives. */
export function updateSession(tokens: AuthTokens): StoredSession {
  const remembered = (() => {
    try {
      return window.localStorage.getItem(KEY) !== null;
    } catch {
      return false;
    }
  })();
  return writeSession(tokens, remembered);
}

export function clearSession(): void {
  for (const store of stores()) {
    try {
      store.removeItem(KEY);
    } catch {
      /* nothing to clear */
    }
  }
}
